import React from "react";
import { TextField, InputAdornment } from "@mui/material";

interface TextInputFieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  required?: boolean;
  type?: string;
  icon?: React.ReactNode;
  error?: boolean;
  helperText?: string;
  maxLength?: number;
  min?: string;
  max?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  validationType?: "name" | "address" | "none";
  multiline?: boolean;
  rows?: number;
  disabled?: boolean;
}

const TextInputField: React.FC<TextInputFieldProps> = React.memo(({
  label,
  name,
  value,
  onChange,
  onBlur,
  required = false,
  type = "text",
  icon,
  error = false,
  helperText = "",
  maxLength,
  min,
  max,
  inputRef,
  validationType = "none",
  multiline = false,
  rows = 4,
  disabled = false,
}) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // For multiline/textarea, pass the native event directly without any modification
    // This preserves all native event properties and prevents focus loss
    if (multiline) {
      // Material-UI TextField sets the name attribute automatically, so we can pass the event as-is
      onChange(e);
      return;
    }

    // For regular input fields, apply validation if needed
    let inputValue = e.target.value;

    if (validationType === "name") {
      // Only allow letters (a-z, A-Z) and spaces - no numbers or special characters
      inputValue = inputValue.replace(/[^a-zA-Z\s]/g, "");
    } else if (validationType === "address") {
      // Allow letters, numbers, and common address symbols
      // Allowed: a-z, A-Z, 0-9, spaces, commas, periods, hyphens, slashes, #, apostrophes, parentheses, colons
      inputValue = inputValue.replace(/[^a-zA-Z0-9\s,.\/#'():\-]/g, "");
    }

    // Only create synthetic event if value changed due to validation
    if (inputValue !== e.target.value) {
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          name: name,
          value: inputValue,
        },
        currentTarget: {
          ...e.currentTarget,
          name: name,
          value: inputValue,
        },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
    } else {
      // No validation needed, but ensure name is set
      const inputEvent = {
        ...e,
        target: {
          ...e.target,
          name: name,
        },
        currentTarget: {
          ...e.currentTarget,
          name: name,
        },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(inputEvent);
    }
  };

  // For datetime-local and date, always shrink the label to prevent overlap
  // Date inputs don't have placeholders, so label should always be shrunk
  const inputLabelProps = (type === "datetime-local" || type === "date") 
    ? { shrink: true } 
    : undefined;

  return (
    <TextField
      fullWidth
      required={required}
      type={type}
      name={name}
      label={label}
      value={value}
      onChange={handleInputChange}
      onBlur={onBlur}
      error={error}
      helperText={helperText}
      disabled={disabled}
      inputRef={inputRef}
      inputProps={{ maxLength, min, max }}
      InputProps={{
        startAdornment: icon ? (
          <InputAdornment position="start">{icon}</InputAdornment>
        ) : undefined,
      }}
      InputLabelProps={inputLabelProps}
      multiline={multiline}
      rows={multiline ? rows : undefined}
    />
  );
});

TextInputField.displayName = 'TextInputField';

export default TextInputField;
